// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  //   Divider,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useEffect, useState } from "react";
import axios from "axios.js";
import { PinInput } from "react-input-pin-code";
import usePageTitle from "app/pageTitle";
import PerfectMoneyHeader from "./header";

function PerfectMoneyBuy() {
  const title = "Buy Perfect Money";
  usePageTitle(title);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [initParams, setInitParams] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [amount, setAmount] = useState(null);
  const [fiatAmount, setFiatAmount] = useState(null);
  const [transactionFee, setTransactionFee] = useState(null);
  const [receivingAccount, setReceivingAccount] = useState(null);
  const [receivingAccountName, setReceivingAccountName] = useState("");
  const [receivingAccountNameFound, setReceivingAccountNameFound] = useState(false);
  const [amountLabel, setAmountLabel] = useState("Amount");
  const [userPin, setUserPin] = useState(null);
  const [pinValues, setPinValues] = useState(["", "", "", ""]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`ewallets/pm/init.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setInitParams(result.data.data);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred. Please try again in a few moments.");
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  function calculateFee(trxAmount) {
    const feeObject = initParams.fee;
    let tFee = feeObject.fee;
    if (feeObject.is_percentage) {
      tFee = trxAmount * feeObject.fee;
      if (feeObject.minimum > 0) {
        tFee = feeObject.minimum > tFee ? feeObject.minimum : tFee;
      }

      if (feeObject.maximum > 0) {
        tFee = feeObject.maximum < tFee ? feeObject.maximum : tFee;
      }

      tFee += feeObject.mandatory_fee;
    }

    let cDiscount = feeObject.discount;
    if (feeObject.is_discount_percentage) {
      cDiscount = tFee * feeObject.discount;
    }

    tFee -= cDiscount;
    return tFee / 100;
  }

  useEffect(() => {
    if (currency === null || amount === null) {
      return;
    }

    let xAmt = (initParams.rates[currency].buy * amount) / 100;
    const fee = calculateFee(xAmt * 100);
    xAmt += fee;
    setFiatAmount(xAmt);
    setTransactionFee(fee);
  }, [currency, amount]);

  function resetPage() {
    setReceivingAccount("");
    setAmountLabel("Amount");
    setAmount("0.00");
  }

  function validateBuyForm() {
    setUserPin(null);
    setPinValues(["", "", "", ""]);

    if (currency === null) {
      setSeverity("error");
      setErrMsg("Select a currency.");
      setOpen(true);
      return;
    }

    if (amount === null || amount === "0.00") {
      setSeverity("error");
      setErrMsg("Enter an amount to buy.");
      setOpen(true);
      return;
    }

    if (receivingAccount === null || receivingAccount === "") {
      setSeverity("error");
      setErrMsg("Enter the receiver's account.");
      setOpen(true);
      return;
    }

    if (!receivingAccountNameFound) {
      setSeverity("error");
      setErrMsg("Unable to retrieve the receiver's account name.");
      setOpen(true);
      return;
    }

    const char1 = receivingAccount.charAt(0);
    if (currency === "USD") {
      if (char1 !== "U") {
        setSeverity("error");
        setErrMsg("Receiver's account must be a USD account.");
        setOpen(true);
        return;
      }
    }

    if (currency === "EUR") {
      if (char1 !== "E") {
        setSeverity("error");
        setErrMsg("Receiver's account must be a EUR account.");
        setOpen(true);
        return;
      }
    }

    setShowConfirmDialog(true);
  }

  async function buy() {
    if (currency === null) {
      setSeverity("error");
      setErrMsg("Select a currency.");
      setOpen(true);
      return;
    }

    if (amount === null || amount === "0.00") {
      setSeverity("error");
      setErrMsg("Enter an amount to buy.");
      setOpen(true);
      return;
    }

    if (receivingAccount === null || receivingAccount === "") {
      setSeverity("error");
      setErrMsg("Enter the receiver's account.");
      setOpen(true);
      return;
    }

    if (!receivingAccountNameFound) {
      setSeverity("error");
      setErrMsg("Unable to retrieve the receiver's account name.");
      setOpen(true);
      return;
    }

    if (userPin === null) {
      setSeverity("error");
      setErrMsg("Enter your pin to authorize this transaction.");
      setOpen(true);
      return;
    }

    const postParams = {
      digital_wallet: "PM",
      receiver_account: receivingAccount,
      amount: amount * 100,
      currency: `${currency}`,
      pin: userPin,
    };

    setShowLoading(true);
    const json = JSON.stringify(postParams);
    await axios
      .post("ewallets/pm/buy.json", json)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          resetPage();
        } else {
          setSeverity("error");
        }
        setErrMsg(result.data.data.message);
        setOpen(true);
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Please try again later.");
        setOpen(true);
      });
  }

  async function getAccountName(account) {
    setReceivingAccountName("");
    setShowLoading(true);
    const postParams = {
      account: `${account}`,
    };
    const json = JSON.stringify(postParams);
    await axios
      .post("ewallets/pm/name_lookup.json", json)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setReceivingAccountName(result.data.data.name);
          setReceivingAccountNameFound(true);
        } else {
          setReceivingAccountNameFound(false);
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Please try again later.");
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={showConfirmDialog}>
        <DialogTitle>Authorize Transaction</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDTypography variant="h6" fontWeight="light">
              Enter your pin to authorize transaction.
            </MDTypography>
            <MDBox mt={2}>
              <PinInput
                values={pinValues}
                onChange={(value, index, values) => setPinValues(values)}
                length={4}
                mask
                autoTab
                placeholder="-"
                onComplete={(text) => {
                  let up = "";
                  text.forEach((itm) => {
                    up += itm;
                  });
                  setUserPin(up);
                }}
              />
            </MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="warning"
            variant="outlined"
            onClick={() => {
              setShowConfirmDialog(false);
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              setShowConfirmDialog(false);
              buy();
            }}
          >
            Proceed
          </MDButton>
        </DialogActions>
      </Dialog>
      <PerfectMoneyHeader />
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox p={3}>
            <MDBox mt={1}>
              <MDBox>
                <MDBox mt={1}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} lg={6} md={6}>
                      <MDBox mb={3}>
                        <MDTypography variant="h6">Payment Instructions</MDTypography>
                      </MDBox>
                      <MDTypography variant="h6" fontWeight="light">
                        Enter the amount, currency and your perfect money receiver account to
                        receive funds instantly.
                      </MDTypography>
                      <MDBox mt={3}>
                        <MDTypography variant="h6" fontWeight="light">
                          <p>Buy Rates</p>
                          {initParams !== null ? (
                            initParams.currencies.map((record) => (
                              <p>
                                <small>
                                  <b>{record}: </b> &nbsp; NGN{" "}
                                  {(initParams.rates[record].buy / 100).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}{" "}
                                  / 1.00 {record}
                                </small>
                              </p>
                            ))
                          ) : (
                            <> </>
                          )}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} lg={6} md={6}>
                      <MDBox>
                        <MDBox mb={3}>
                          <MDTypography variant="h6">Deposit Order</MDTypography>
                        </MDBox>
                        <Grid container spacing={1}>
                          <Grid item xs={4} md={4} lg={4}>
                            <FormControl sx={{ width: "100%" }}>
                              <InputLabel>Currency</InputLabel>
                              <Select
                                label="Currency"
                                sx={{ padding: "12px", paddingLeft: "3px" }}
                                onChange={(e) => {
                                  setCurrency(e.target.value);
                                  setAmountLabel(`${e.target.value} Amount`);
                                }}
                              >
                                {
                                  // --> Notice below changes
                                  initParams !== null && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                                    initParams.currencies.map((record) => (
                                      <MenuItem value={record}>{record}</MenuItem>
                                    ))
                                }
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={8} md={8} lg={8}>
                            <MDInput
                              onChange={(e) => {
                                setAmount(e.target.value);
                              }}
                              label={amountLabel}
                              sx={{ width: "100%" }}
                              type="decimal"
                              value={amount}
                            />
                          </Grid>
                        </Grid>
                      </MDBox>
                      <MDBox mt={3}>
                        <MDInput
                          sx={{ width: "100%" }}
                          onChange={(e) => {
                            setReceivingAccount(e.target.value);
                          }}
                          label="Receiver's Account"
                          value={receivingAccount}
                          onBlur={(e) => {
                            getAccountName(e.target.value);
                          }}
                        />
                      </MDBox>
                      <MDBox mt={3}>
                        <MDInput
                          readOnly
                          disabled
                          sx={{ width: "100%" }}
                          label="Receiver's Account Name"
                          value={receivingAccountName}
                        />
                      </MDBox>

                      <MDBox mt={3}>
                        {fiatAmount !== null ? (
                          <MDBox mt={2}>
                            <Alert severity="info" sx={{ width: "100%" }}>
                              <MDBox>
                                <small variant="caption" fontWeight="heavy">
                                  Ex. Rate:{" "}
                                  {(initParams.rates[currency].buy / 100).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </small>
                              </MDBox>
                              <MDBox>
                                <small variant="caption" fontWeight="medium">
                                  Fee: NGN{" "}
                                  {transactionFee.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </small>
                              </MDBox>
                              <MDBox>
                                <small variant="caption" fontWeight="medium">
                                  You Pay: NGN{" "}
                                  {fiatAmount.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </small>
                              </MDBox>
                            </Alert>
                          </MDBox>
                        ) : (
                          <> </>
                        )}
                      </MDBox>
                      <MDBox mt={4} mb={2} align="right">
                        <MDButton
                          color="info"
                          variant="contained"
                          onClick={() => {
                            validateBuyForm();
                          }}
                          sx={{ align: "right" }}
                        >
                          Deposit Funds
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PerfectMoneyBuy;
